import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import * as fromTaskActions from '../actions/tasks.actions';

import { Observable, of, filter } from 'rxjs';
import { map, catchError, withLatestFrom, switchMap, take } from 'rxjs/operators';
import { FirebaseService } from '../../services/firebase/firebase.service';
import { DocumentData, DocumentReference, Firestore, Query, collection, doc, query, where } from '@angular/fire/firestore';
import { Task } from "../../models/task.model";

@Injectable()
export class TaskEffects {

    constructor(
        private readonly action$: Actions,
        private readonly firestore: Firestore,
        private readonly firebaseService: FirebaseService,
        private readonly store: Store<{ taskData: { data: Task[] } }>
    ) { }

    // Load Task
    loadTask$: Observable<Action> = createEffect(() => this.action$.pipe(
        ofType(fromTaskActions.LoadTasksAction),
        switchMap(({ month, year, userId }: { type: string; month: number; year: number; userId: string; }) => {
            const dateIni =  new Date(year, month - 1, 1, 0, 0, 0, 0);
            const dateEnd = new Date(year, month, 0, 23, 59, 59, 999);
            const documentRef: DocumentReference = doc(this.firestore, `users/${userId}`);
            const ref: Query<DocumentData, DocumentData> = query(
                collection(this.firestore, 'events'),
                where('start', '>=', dateIni),
                where('start', '<=', dateEnd),
                where('attendees', 'array-contains', documentRef)
            );
            return this.firebaseService.getDataOfCollectionByField(ref).pipe(take(1), map((resp) => resp.map((event: any) => {
                delete event.trainerRef;
                delete event.attendees;

                return {
                    ...event,
                    start: new Date(event.start.seconds * 1000),
                    end: new Date(event.end.seconds * 1000)
                };
            })));
        }),
        map((data) => ({ type: fromTaskActions.LOAD_TASKS_SUCCESS, data: data })),
        catchError(error => of({ type: fromTaskActions.LOAD_TASKS_FAIL, error: error }))
    ));

    // Add Task
    addTask$: Observable<Action> = createEffect(() => this.action$.pipe(
        ofType(fromTaskActions.AddTaskAction),
        withLatestFrom(this.store.select('taskData').pipe(filter((taskData) => !!taskData.data), take(1))),
        map(([action, taskData]) => {
            const newData = [...taskData.data];
            newData.push(action.data);
            return ({ type: fromTaskActions.ADD_TASK_SUCCESS, data: newData })
        }),
        catchError(error => of({ type: fromTaskActions.ADD_TASK_FAIL, error: error }))
    ));

    // Delete Task
    deleteTask$: Observable<Action> = createEffect(() => this.action$.pipe(
        ofType(fromTaskActions.DeleteTaskAction),
        withLatestFrom(this.store.select('taskData').pipe(filter((taskData) => !!taskData.data), take(1))),
        map(([action, taskData]) => {
            const newData = [...taskData.data];
            const result = newData.findIndex((data) => data.id === action.data.id)
            newData.splice(result, 1)
            return ({ type: fromTaskActions.DELETE_TASK_SUCCESS, data: newData })
        }),
        catchError(error => of({ type: fromTaskActions.DELETE_TASK_FAIL, error: error }))
    ));

    // Update Task
    updateTask$: Observable<Action> = createEffect(() => this.action$.pipe(
        ofType(fromTaskActions.UpdateTaskAction),
        withLatestFrom(this.store.select('taskData').pipe(filter((taskData) => !!taskData.data), take(1))),
        map(([action, taskData]) => {
            const newData = [...taskData.data];
            const result = newData.findIndex((data) => data.id === action.data.id)
            newData[result] = action.data;
            return ({ type: fromTaskActions.UPDATE_TASK_SUCCESS, data: newData })
        }),
        catchError(error => of({ type: fromTaskActions.UPDATE_TASK_FAIL, error: error }))
    ));
}